
import { useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { RecoveryCode } from '../../types';
import { supabase } from '@/integrations/supabase/client';
import { MfaError, handleMfaError } from '../types';

/**
 * Hook for MFA recovery operations
 * 
 * Provides functionality to:
 * - Generate recovery codes for an MFA-enabled account
 * - Recover account access using recovery codes when authenticator device is lost
 * 
 * @returns MFA recovery functions
 */
export function useMfaRecovery() {
  const { setIsLoading } = useBaseMfaState();

  /**
   * Generate recovery codes for an MFA-enabled account
   * 
   * This function calls our database function to generate and store recovery codes securely.
   * 
   * @returns Promise resolving to array of recovery codes or null on failure
   */
  const generateRecoveryCodes = async (): Promise<RecoveryCode[] | null> => {
    try {
      setIsLoading(true);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');
      
      // Call our database function to generate recovery codes
      const { data, error } = await supabase.rpc('generate_recovery_codes', {
        user_uuid: user.id
      });
      
      if (error) throw error;
      
      // Map the generated codes to our RecoveryCode type
      const recoveryCodes: RecoveryCode[] = (data || []).map((code: string) => ({
        code,
        createdAt: new Date().toISOString()
      }));
      
      toast({
        title: "Recovery Codes Generated",
        description: "Please store these recovery codes in a safe place. You won't be able to see them again.",
      });
      
      return recoveryCodes;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('Recovery code generation failed:', mfaError);
      toast({
        title: "Recovery Code Generation Failed",
        description: mfaError.message || "An unknown error occurred",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Recover MFA account access using a recovery code
   * 
   * This function:
   * 1. Validates the recovery code format
   * 2. Checks if the code exists and hasn't been used
   * 3. Marks the code as used and disables MFA temporarily
   * 4. Provides user feedback through toast notifications
   * 
   * @param recoveryCode - Recovery code provided by the user
   * @returns Promise resolving to boolean indicating recovery success
   */
  const recoverMfaAccount = async (recoveryCode: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Validate the recovery code format
      if (!recoveryCode || recoveryCode.trim().length < 6) {
        throw new Error("Invalid recovery code format");
      }
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');
      
      // Check if the recovery code exists and hasn't been used
      const { data: codeData, error: codeError } = await supabase
        .from('mfa_recovery_codes')
        .select('*')
        .eq('user_id', user.id)
        .eq('code', recoveryCode.trim().toUpperCase())
        .is('used_at', null)
        .single();
      
      if (codeError || !codeData) {
        throw new Error("Invalid or already used recovery code");
      }
      
      // Mark the recovery code as used
      const { error: markUsedError } = await supabase
        .from('mfa_recovery_codes')
        .update({ used_at: new Date().toISOString() })
        .eq('id', codeData.id);
      
      if (markUsedError) {
        console.warn('Failed to mark recovery code as used:', markUsedError);
      }
      
      // Disable MFA factors temporarily (user will need to re-enable)
      const { error: disableError } = await supabase
        .from('mfa_factors')
        .update({ status: 'disabled' })
        .eq('user_id', user.id);
      
      if (disableError) {
        console.warn('Failed to disable MFA factors:', disableError);
      }
      
      toast({
        title: "MFA Recovery Successful",
        description: "Your account has been recovered. Please set up MFA again for security.",
      });
      
      return true;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA recovery failed:', mfaError);
      toast({
        title: "MFA Recovery Failed",
        description: mfaError.message || "Invalid recovery code. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    generateRecoveryCodes,
    recoverMfaAccount
  };
}
